import {
    addLineItemsType,
    addSelectionFields,
    addTableColumnExtensions,
    addEntityPropertiesToEnum,
    addStandardHeaderActions,
    getLineItemAnnotation,
    getUIAnnotationForEntitySet,
    getExportTablesPropertiesAdapter
} from './utils';
import { findListReportPageV2 } from '../import';
import {
    DEFINITION_LINK_PREFIX,
    addFieldsType,
    addEnumForValidAnnotations,
    addEnumForSingleTabVariant,
    addEnumForMultiTabVariant,
    addEnumForVariantEntitySet,
    addEnumFieldGroupAnnotationPath,
    updatePropertyIndices,
    createAnnotationPath
} from '../../common';
import type { ManifestPathParams } from '../../common';
import i18next from 'i18next';
import { ManifestSection, DefinitionName, LogSeverity, MANIFESTPATH, SchemaTag } from '@sap/ux-specification-types';
import type { v2 } from '@sap/ux-specification-types';
import { log } from '../../../extensionLogger';
import { LINEITEMTABLETYPES } from '../types';
import type { ConvertedMetadata, EntitySet, EntityType } from '@sap-ux/vocabularies-types';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import type { Definition } from 'typescript-json-schema';
import type { GenerateAppSchemaParameters } from '@sap/ux-specification-types';
import { addManifestPathsToProperties, getPageManifestPath } from '../utils';
import { pageTypes } from '../export/factory';

const ROOT_PROPERTIES_ORDER: Array<keyof v2.ListReportConfigV2> = ['filterBar', 'table', 'footer'];

/**
 * Adds the enum types for QuickVariant and QuickVariantX, based on the entity type annotations.
 *
 * @param {EntityType} entityType - The entity type containing annotations to be processed
 * @param {EntitySet} entitySet - current entity set of the page or view
 * @param {Definition} appSchema - app specific JSON schema
 * @param {ConvertedMetadata} oDataServiceAVT - combined service metadata, as returned by annotation vocabularies tools
 */
export function addEnumForVariantPaths(
    entityType: EntityType,
    entitySet: EntitySet,
    appSchema: Definition,
    oDataServiceAVT: ConvertedMetadata
): void {
    addEnumForSingleTabVariant(entityType, appSchema, 'QuickVariant');
    addEnumForMultiTabVariant(appSchema, 'QuickVariantX', entitySet);
    addEnumForVariantEntitySet(oDataServiceAVT, appSchema, 'QuickVariantX');
}

/**
 * Adds an enum filled with existing UI annotations for annotationPath property in FilterBar.
 *
 * @param entityType - The entity type containing annotations to be processed
 * @param appSchema - app specific JSOn schema
 * @param definitionName - name of the definition in the app schema
 */
function addEnumForFilterBarAnnotationPath(
    entityType: EntityType,
    appSchema: Definition,
    definitionName: string
): void {
    const annoPath = (appSchema.definitions[definitionName] as Definition).properties.annotationPath as Definition;
    const validAnnotations = [
        UIAnnotationTerms.SelectionPresentationVariant,
        UIAnnotationTerms.SelectionVariant,
        UIAnnotationTerms.PresentationVariant
    ];
    addEnumForValidAnnotations(entityType, validAnnotations, annoPath);
}

/**
 * Adds 'manifestPath' to each property in a definition based on metadata sync rules.
 *
 * @param appSchema - Full schema.
 * @param definitionName - The name of the definition/class whose properties are being processed.
 * @param definition - The definition object containing property schemas.
 * @param pathParams - Additional context required to compute manifest paths.
 */
function addManifestPathsToPropertiesLR(
    appSchema: Definition,
    definitionName: string | undefined,
    definition: Definition,
    pathParams: ManifestPathParams
): void {
    let targetAnnotation: string | undefined = undefined;
    if (pathParams.targetAnnotation) {
        const annotationParts = pathParams.targetAnnotation?.split('/');
        targetAnnotation = annotationParts[annotationParts.length - 1];
    } else {
        // Default annotation path
        targetAnnotation = `@${UIAnnotationTerms.LineItem}`;
    }
    addManifestPathsToProperties(
        appSchema,
        definitionName,
        definition,
        {
            ...pathParams,
            pathsParts: [targetAnnotation, '', targetAnnotation]
        },
        pageTypes.ListReport
    );
}

/**
 * Adds 'manifestPath' to static nodes in schema.
 *
 * @param appSchema - Full schema.
 * @param pathParams - Additional context required to compute manifest paths.
 */
function addManifestPathsToStaticNodes(appSchema: Definition, pathParams: ManifestPathParams): void {
    // Add "manifestPath" for current page
    appSchema['manifestPath'] = getPageManifestPath(pathParams);
    // Add manifestPath to root level
    addManifestPathsToPropertiesLR(appSchema, 'ListReport', appSchema, pathParams);
    // Add manifestPath to table
    addManifestPathsToPropertiesLR(appSchema, undefined, appSchema.properties.table as Definition, pathParams);
    // Add manifestPath to filterBar
    addManifestPathsToPropertiesLR(appSchema, undefined, appSchema.properties.filterBar as Definition, pathParams);
}

/**
 * Generates an app specific schema for the FE V2 ListReport from the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param {object} genericSchema - generic JSON schema of a list report
 * @returns appSchema - the application specific JSON schema
 */
export function generateListReportSchemaV2(
    generateParameters: GenerateAppSchemaParameters,
    genericSchema: Definition
): object {
    const appSchema = JSON.parse(JSON.stringify(genericSchema)) as Definition;

    const lrLineItems = {
        $ref: DEFINITION_LINK_PREFIX + DefinitionName.LineItems
    };
    const lrToolBar = DEFINITION_LINK_PREFIX + DefinitionName.ToolBarLR;

    const exportTablePropertiesAdapter = getExportTablesPropertiesAdapter(generateParameters.flex ?? []);

    LINEITEMTABLETYPES.forEach((tableType, i) => {
        appSchema.properties.table['anyOf'][i.toString()]['$ref'] = DEFINITION_LINK_PREFIX + tableType;
        const tableTypeRaw = tableType.replace('<LineItems>', '');
        const localGenericSchema = JSON.parse(JSON.stringify(genericSchema.definitions[tableTypeRaw]));
        localGenericSchema.properties.columns = lrLineItems;
        localGenericSchema.properties.toolBar['$ref'] = lrToolBar;
        exportTablePropertiesAdapter(localGenericSchema);
        appSchema.definitions[tableType] = localGenericSchema;
    });

    const { entityType, uIAnnotations } = getUIAnnotationForEntitySet(generateParameters); // would log an error in case these entities are missing
    if (uIAnnotations) {
        // then entityType is there as well
        // retrieve page definition of root page from manifest
        const pageKeys = [];
        const v2Page = findListReportPageV2(
            generateParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
            pageKeys
        );

        // handle standard header actions
        addStandardHeaderActions(appSchema);
        //handle line items
        const lineItemAnnotation = getLineItemAnnotation(
            uIAnnotations,
            v2Page?.component?.settings,
            generateParameters.logger
        );
        if (lineItemAnnotation) {
            const annotationPath = createAnnotationPath(
                entityType.fullyQualifiedName,
                lineItemAnnotation.term,
                lineItemAnnotation.qualifier
            );
            LINEITEMTABLETYPES.forEach((tableType) => {
                const tableDefinition = appSchema.definitions[tableType] as Definition;
                tableDefinition[SchemaTag.annotationPath] = annotationPath;
            });
        }
        addLineItemsType(false, appSchema, lineItemAnnotation, entityType);
        //handle filter fields
        const selectionFieldAnnotation = uIAnnotations.SelectionFields;
        addSelectionFields(appSchema, selectionFieldAnnotation, entityType);

        //add fields for createWithParameterDialog
        addFieldsType(appSchema, entityType);

        // add columns to a enum, in this case MultiEdit -> ignoredFields
        (appSchema.definitions.MultiEdit as Definition).properties.ignoredFields['items']['enum'] = [];
        addEntityPropertiesToEnum(
            (appSchema.definitions.MultiEdit as Definition).properties.ignoredFields['items']['enum'],
            entityType
        );

        if (!v2Page) {
            log(generateParameters.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOLR'),
                location: {
                    path: MANIFESTPATH,
                    range: [ManifestSection.generic]
                }
            });
            return appSchema;
        }
        addTableColumnExtensions(
            appSchema,
            entityType,
            v2Page,
            generateParameters.manifest,
            generateParameters.fragments
        );

        addEnumForVariantPaths(entityType, generateParameters.entitySet, appSchema, generateParameters.serviceAVT);
        addEnumForFilterBarAnnotationPath(entityType, appSchema, DefinitionName.ListReportFilterBar);
        addEnumFieldGroupAnnotationPath(entityType, appSchema, DefinitionName.MultiEditV2);
        // Add manifest path to static nodes in schema
        addManifestPathsToStaticNodes(appSchema, {
            manifest: generateParameters.manifest,
            pageName: pageKeys
        });

        //Delete generic definitions
        delete appSchema.definitions.ResponsiveTableWithMultiSelect;
        delete appSchema.definitions.ResponsiveTableWithInlineDelete;
        delete appSchema.definitions.TreeTable;
        delete appSchema.definitions.AnalyticalTable;
        delete appSchema.definitions.GridTable;
        delete appSchema.definitions.GenericColumns;
        delete appSchema.definitions.TableColumnAction;
        delete appSchema.definitions.ToolBar;
        delete appSchema.definitions.Actions;
        delete appSchema.definitions.GenericFooter;
        delete appSchema.definitions.FieldPath;
        delete appSchema.definitions.Share;
    }
    updatePropertyIndices(appSchema, ROOT_PROPERTIES_ORDER);
    return appSchema;
}
